import React from 'react';
import { CheckCircle2, Download } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { jsPDF } from "jspdf";
import { useTranslation } from 'react-i18next';

interface Props {
  allStepData?: any;
  stepHelpers?: any;
  onGoToStep?: (step: number) => void;
}

export const Confirmation: React.FC<Props> = ({ allStepData, stepHelpers, onGoToStep }) => {
  const { t } = useTranslation();
  const accountData = allStepData?.[0]?.accountData;
  const paymentDetails = allStepData?.[2]?.paymentDetails;
  const handleDownloadReceipt = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(22);
    doc.text(t("Payment Receipt"), 105, 20, { align: "center" });
    
    doc.setFontSize(12);
    doc.text(`${t("Transaction ID")}: ${paymentDetails?.transactionId || t("N/A")}`, 20, 40);
    doc.text(`${t("Account Name")}: ${accountData?.name || t("N/A")}`, 20, 50);
    doc.text(`${t("Consumer No.")}: ${accountData?.consumerNo || t("N/A")}`, 20, 60);
    
    doc.line(20, 65, 190, 65);
    
    doc.text(`${t("Bills Paid")}: ${t("{{count}} bill(s)", { count: paymentDetails?.billsCount || 0 })}`, 20, 75);
    doc.text(`${t("Amount Paid")}: $${paymentDetails?.amount?.toFixed(2) || '0.00'}`, 20, 85);
    doc.text(`${t("Date & Time")}: ${paymentDetails?.date || t("N/A")}`, 20, 95);
    doc.text(`${t("Payment Method")}: ${paymentDetails?.method || t("N/A")}`, 20, 105);
    
    doc.line(20, 110, 190, 110);
    
    doc.setFontSize(14);
    doc.text(t("Payment Successful!"), 105, 125, { align: "center" });

    doc.save(`Receipt_${paymentDetails?.transactionId || 'Payment'}.pdf`);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column */}
      <div className="flex-1">
        <div className="bg-white rounded-2xl p-8 border border-green-200 shadow-sm text-center">
          
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("Payment Successful!")}</h2>
          <p className="text-gray-500 mb-10">{t("Your bill payment has been processed successfully.")}</p>

          <div className="bg-gray-50 rounded-xl p-6 max-w-lg mx-auto border border-gray-100">
            <div className="space-y-4 text-sm text-left">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 font-medium">{t("Consumer No.")}</span>
                <span className="font-semibold text-gray-900">{accountData?.consumerNo}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 font-medium">{t("Bills Paid")}</span>
                <span className="font-semibold text-gray-900">{t("{{count}} bill(s)", { count: paymentDetails?.billsCount || 0 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 font-medium">{t("Amount Paid")}</span>
                <span className="font-bold text-green-600 text-base">${paymentDetails?.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 font-medium">{t("Date & Time")}</span>
                <span className="font-semibold text-gray-900">{paymentDetails?.date}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 font-medium">{t("Payment Method")}</span>
                <span className="font-semibold text-gray-900">{paymentDetails?.method}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <Button className="gap-2" onClick={handleDownloadReceipt}>
              <Download className="w-4 h-4" /> {t("Download Receipt")}
            </Button>

            <Button 
            variant='outline'
              onClick={() => {
                stepHelpers?.resetAllData();
                onGoToStep?.(0);
              }}
              className=""
            >
              {t("Pay Another Bill")}
            </Button>
          </div>
        </div>
      </div>


    </div>
  );
};
